import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';

import { ACCENT_SX, BulkRunProps } from './dialogShared';
import { BulkRunStatus } from '../../../components/molecules/BulkRunStatus';
import { StatusOption } from '../useListJobActions';

export const StatusDialog: React.FC<
  {
    open: boolean;
    isBulk: boolean;
    count: number;
    /** Bulk runs skip interaction forms; say so before the user commits. */
    bypassesInteractions?: boolean;
    /** Distinct service types among the selected jobs. */
    serviceTypeNames?: string[];
    options: StatusOption[];
    loading: boolean;
    submitting: boolean;
    lastError: string | null;
    onClose: () => void;
    onSubmit: (code: string, notes: string) => Promise<void>;
  } & BulkRunProps
> = ({
  open,
  isBulk,
  count,
  options,
  loading,
  submitting,
  lastError,
  onClose,
  onSubmit,
  progress,
  failures,
  bypassesInteractions,
  serviceTypeNames,
}) => {
  const [selected, setSelected] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setSelected('');
      setNotes('');
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    if (selected) onSubmit(selected, notes);
  }, [selected, notes, onSubmit]);

  const title = isBulk ? `Update Status (${count} jobs)` : 'Update Status';

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-label={title}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {(serviceTypeNames?.length ?? 0) > 1 && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: '12px' }}>
            {serviceTypeNames?.length} service types selected ({serviceTypeNames?.join(', ')}). Only
            statuses every selected job can reach are offered — update jobs of one service type
            together for the full list.
          </Alert>
        )}
        {bypassesInteractions && (
          <Alert severity="info" sx={{ mb: 2, fontSize: '12px' }}>
            Interaction forms configured against these jobs will not be collected.
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : options.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', py: 2 }}>
            No available status transitions.
          </Typography>
        ) : (
          <RadioGroup
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label="Status options"
          >
            {options.map((opt) => (
              <FormControlLabel
                key={opt.code}
                value={opt.code}
                control={<Radio />}
                label={opt.name}
              />
            ))}
          </RadioGroup>
        )}
        <TextField
          label="Notes"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
          aria-label="Status change notes"
        />
        <BulkRunStatus
          progress={progress}
          submitting={submitting}
          failures={failures}
          error={lastError}
          verb="Updating"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel status update">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selected || submitting}
          sx={ACCENT_SX}
          aria-label="Submit status update"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
